import React from "react";

import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";

import LinearGradientBox from "@app/analytics/components/LinearGradientBox";
import Marker from "@app/analytics/components/Marker";
import READ_AVERAGES from "@app/analytics/constants/readAverages";
import MetricLockChip from "@app/analytics/MeetingReport/components/MetricLockChip";
import { StandingsMetricDataWithScores } from "@app/analytics/MeetingReport/types";
import { PartialParsedMetric } from "@app/analytics/utils";

import Stack, { Row } from "@rui/Stack";

import { Container } from "./styles";

interface StandingsCardProps extends StandingsMetricDataWithScores {
  subtitle?: string;
  locked?: boolean;
}

const MIN_LEFT_VALUE = 5;
const MAX_LEFT_VALUE = 95;

const METRIC_TYPES = {
  point: " Avg",
  percentage: "%",
  duration: "m",
};

const normalize = (v: number | null, minValue: number) =>
  !v ? 0 : ((v - minValue) * 100) / (100 - minValue);

const StandingsCard = ({
  id,
  title,
  subtitle,
  minValue = 0,
  score,
  average,
  type,
  fullValue,
  locked = false,
}: StandingsCardProps) => {
  const calculatedScore =
    score == null
      ? null
      : Math.min(
          100,
          fullValue && score ? (score / fullValue) * 100 : score || 0
        );

  const calculatedAverage = Math.min(
    100,
    fullValue && average ? (average / fullValue) * 100 : average || 0
  );

  const readAverageValue =
    (id && (READ_AVERAGES[id] as PartialParsedMetric)?.value) ?? null;

  const normalizedReadAverage = normalize(readAverageValue, minValue);
  const normalizedCalculatedAverage = normalize(calculatedAverage, minValue);
  const normalizedCalculatedScore = normalize(calculatedScore, minValue);

  const labelSuffix = (type ? METRIC_TYPES[type] : "") ?? "";

  return (
    <Container spacing={3}>
      <Stack spacing={0.75}>
        <Typography variant="bodyPrimaryMed" color="shades.neutral.800">
          {title}
        </Typography>
        <Typography variant="labelReg" color="shades.neutral.500">
          {subtitle}
        </Typography>
      </Stack>
      {locked ? (
        <MetricLockChip />
      ) : (
        <Row variant="apart" spacing={1}>
          <Typography
            variant="bodySecondaryReg"
            sx={{ color: "shades.neutral.700", minWidth: "fit-content" }}
          >
            {minValue}
            {labelSuffix}
          </Typography>
          <Stack sx={{ position: "relative" }}>
            <LinearProgress
              value={normalizedCalculatedAverage}
              variant="determinate"
              sx={{
                minWidth: "145px",
                height: "8px",
                backgroundColor: "shades.neutral.100",
                boxShadow: "inset 0px 4px 4px rgba(58, 59, 66, 0.12)",
                borderRadius: "2px",
                "&>span": {
                  backgroundColor: "shades.primary.800",
                },
              }}
            />
            {readAverageValue && (
              <LinearGradientBox
                sx={{
                  position: "absolute",
                  mt: "-5px",
                  left: `${normalizedReadAverage}%`,
                  height: "18px",
                  width: "18px",
                }}
              />
            )}
            {calculatedScore != null && (
              <Marker
                sx={{ top: -26 }}
                label={`${calculatedScore}${labelSuffix}`}
                value={Math.min(
                  Math.max(MIN_LEFT_VALUE, normalizedCalculatedScore),
                  MAX_LEFT_VALUE
                )}
              />
            )}
          </Stack>
          <Typography
            variant="bodySecondaryReg"
            sx={{ color: "shades.neutral.700", minWidth: "35px" }}
          >
            100
            {labelSuffix}
          </Typography>
        </Row>
      )}
    </Container>
  );
};

export default StandingsCard;
